/**
 * Тариф и подписка.
 *
 * Экран собирается **одним запросом** `GET /billing/portal`: у ЮKassa нет
 * портала управления подпиской, как у Stripe, поэтому управление живёт у нас, и
 * сервер отдаёт сразу подписку, квоты, расход периода, докупки, автосписание,
 * платежи и документы. Собирать это шестью запросами значит показать экран,
 * половина которого противоречит второй половине.
 *
 * Три вещи, которые здесь важнее вёрстки.
 *
 * 1. **Расход считается по расчётному периоду, а не по календарному месяцу.**
 *    Иначе смена тарифа в середине месяца делает цифру расхода
 *    неинтерпретируемой.
 * 2. **Скрытая квота названа вслух.** Объём ответов движков за цикл клиент не
 *    покупал и на витрине не видел, но именно он останавливает замер. Продукт,
 *    который меряет, не имеет права останавливаться по невидимой причине.
 * 3. **Понижение тарифа не применяется мгновенно.** Оно планируется на
 *    следующий период, потому что расход текущего уже случился; экран говорит
 *    об этом до нажатия, а не после.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { KvRow, Kv, Panel } from '../ui/form';
import { Meter } from '../ui/Meter';
import { Dialog } from '../ui/Dialog';
import { Confirm } from '../ui/fields';
import { ErrorNotice, Skeleton } from '../ui/state';
import { useConfirmed, StepUpCancelled } from '../ui/StepUp';
import { ApiError } from '../api/problem';
import { useAccess } from '../session';
import {
  addAddon,
  cancelPendingPlanChange,
  cancelSubscription,
  changePlan,
  cancelPayment,
  deletePaymentMethod,
  downloadDocument,
  getBillingPortal,
  getPlanCatalog,
  idempotencyKey,
  previewPlanChange,
  rememberPayment,
  removeAddon,
  resumeSubscription,
  revokeAutopayConsent,
  type BillingPeriodUnit,
  type BillingPortal,
  type Plan as PlanRow,
  type Payment,
  type PlanCatalog,
  type PlanChangePreview,
} from '../api/billing';
import {
  countOf,
  dateProse,
  moneyKopecks,
  momentProse,
  daysLeft,
  int,
  NOT_MEASURED,
} from '@shared/src/format';
import { LEGAL_ENTITY, PAYER_SNAPSHOT } from '@shared/src/product';
import { scanBlockedFallback } from '../ui/screen-states';

/**
 * Состояние документа словами.
 *
 * Отдельной таблицей, а не тернарником по месту: состояний четыре, и
 * аннулированный счёт, показанный «выставленным», — это платёжное поручение по
 * документу, который мы уже отозвали. Перевыставление счёта такое состояние
 * создаёт штатно, значит показывать его обязаны честно.
 */
const DOCUMENT_STATE: Record<string, string> = {
  draft: 'черновик',
  issued: 'выставлен',
  paid: 'оплачен',
  cancelled: 'аннулирован',
};

type Load =
  | { kind: 'loading' }
  /**
   * Каталог докупок приезжает рядом с порталом.
   *
   * Портал отдаёт ПОДКЛЮЧЁННЫЕ докупки (`addons`), а не продаваемые. Список
   * продаваемых был захардкожен двумя кнопками — `weekly_50` и `daily_25`, — и
   * половина позиций §10 не продавалась вовсе: «+200 вопросов в недельную
   * перепись» за 15 900 ₽/мес недоступна была никак, а это самая дорогая
   * позиция и прямой выход из исчерпанной квоты «Роста» и «Масштаба».
   */
  | { kind: 'ready'; portal: BillingPortal; catalog: PlanCatalog | null }
  | { kind: 'failed'; error: unknown };

/**
 * Галочка в списке возможностей тарифа. Контракт иконок системы: 16/20/24,
 * толщина 2, `currentColor` — свой цвет иконке здесь не задаётся, иначе значок
 * превращается во второй акцент на карточке (components.md).
 */
function Check() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 12l4 4 10-10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Порядок тарифов в сетке. В контракте поле необязательное. */
const order = (plan: { sort_order?: number }) => plan.sort_order ?? 0;

/** Состояния подписки по-русски. Технический код клиенту не показывается. */
const STATUS_WORDS: Record<string, string> = {
  pending: 'Ждём первый платёж',
  active: 'Действует',
  past_due: 'Платёж не прошёл',
  cancelled: 'Отменена',
  expired: 'Истекла',
};

export function Plan() {
  const access = useAccess();
  const confirmed = useConfirmed();

  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const [period, setPeriod] = useState<BillingPeriodUnit>('month');
  const [cancelling, setCancelling] = useState(false);
  /**
   * Два действия, которые меняют деньги, спрашиваются ДО нажатия.
   *
   * Отзыв согласия снимает автопродление, отключение докупки урезает лимиты, и
   * оба срабатывали молча — а сказано о последствии было тостом ПОСЛЕ. Рядом на
   * этом же экране девять диалогов подтверждения, каждый из которых называет
   * последствие заранее.
   */
  const [revokingConsent, setRevokingConsent] = useState(false);
  /**
   * Отвязка карты — отдельное состояние, а не второй смысл отзыва согласия.
   *
   * Отзыв согласия гасит согласие по 376-ФЗ и оставляет карту сохранённой у
   * провайдера; отвязка убирает саму карту. Пока в кабинете была одна кнопка,
   * убрать карту без письма в поддержку было нельзя, а ЮKassa включает
   * рекуррентные платежи только магазину, где клиент делает это сам.
   */
  const [deletingMethod, setDeletingMethod] = useState(false);
  const [removingAddon, setRemovingAddon] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  /**
   * Задуманная смена тарифа, показанная человеку до всякого списания.
   *
   * Приёмка: «Перейти сейчас» на карточке «Рост» открывало ЮKassa на
   * 14 826,10 ₽ — одним щелчком, без промежуточного экрана. Сумма впервые
   * называлась уже у провайдера, откуда она берётся, узнать было неоткуда, а
   * что меняется в лимитах, не говорилось нигде. Это покупка, и она обязана
   * иметь шаг, на котором видно цену и последствия.
   *
   * Три состояния, а не «открыто/закрыто»: расчёт делает сервер, а значит его
   * можно ждать и он может не прийти. Показывать пустое окно или, хуже,
   * подставлять посчитанное на клиенте — значит вернуть ту же беду в другом
   * оформлении.
   */
  const [intent, setIntent] = useState<
    | { kind: 'counting'; plan: PlanRow }
    | { kind: 'ready'; plan: PlanRow; preview: PlanChangePreview }
    | { kind: 'failed'; plan: PlanRow; error: unknown }
    | null
  >(null);
  /**
   * Номер последнего запрошенного расчёта.
   *
   * Расчёт идёт по сети, а окно закрывается мгновенно. Без этого счётчика
   * опоздавший ответ переписывал состояние: закрыл окно — оно открылось снова
   * само; открыл «Масштаб» поверх «Роста» — увидел цифры «Роста» под заголовком
   * «Масштаб». Последнее хуже всего: подтверждение ушло бы на тариф, цену
   * которого человек не видел.
   */
  const asked = useRef(0);

  useEffect(() => {
    const abort = new AbortController();
    Promise.all([
      getBillingPortal(abort.signal),
      // Каталог публичный; если он не доехал, экран тарифа всё равно обязан
      // открыться — просто без списка докупок.
      getPlanCatalog(abort.signal).catch(() => null),
    ])
      .then(([portal, catalog]) => {
        setLoad({ kind: 'ready', portal, catalog });
        if (portal.subscription) setPeriod(portal.subscription.period);
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });
    return () => abort.abort();
  }, [attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  /**
   * Одно место, где живёт «нажали — сходили — перечитали». Ошибка действия
   * показывается рядом с экраном и **не стирает** уже показанные числа: они
   * верны, просто действие не удалось.
   */
  const act = useCallback(
    async (key: string, run: () => Promise<string | null>) => {
      setBusy(key);
      setFailure(null);
      setNote(null);
      try {
        setNote(await run());
        reload();
      } catch (error) {
        if (error instanceof StepUpCancelled) return;
        setFailure(error);
      } finally {
        setBusy(null);
      }
    },
    [reload],
  );

  // Подписи «пробного периода нет» здесь не было и не будет: это довод для того,
  // кто ещё выбирает, и живёт он на странице тарифов сайта. Клиенту, открывшему
  // свой тариф, отрицание в шапке не сообщает ничего, а читается как записка.
  const head = (
    <div className="screen-head">
      <h1>Тариф</h1>
    </div>
  );

  if (load.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }

  if (load.kind === 'failed') {
    return (
      <>
        {head}
        <ErrorNotice
          error={load.error}
          action={
            <button className="btn btn-ghost btn-sm" type="button" onClick={reload}>
              Повторить
            </button>
          }
        />
      </>
    );
  }

  const { portal, catalog } = load;
  const subscription = portal.subscription ?? null;
  const quotas = portal.quotas;
  const plans = (portal.available_plans ?? [])
    .slice()
    .sort((a, b) => order(a) - order(b));
  const current = plans.find((p) => p.code === subscription?.plan_code) ?? null;
  const answers = (portal.usage?.counters ?? []).find((c) => c.metric === 'answers');
  // Автопродление считается включённым, только когда согласие действует И оно
  // не выключено: без обоих условий деньги не спишутся, и говорить о списании
  // нельзя ни в сводке, ни в панели.
  const autopayOn = Boolean(portal.autopay?.enabled && portal.autopay.consent_granted);
  // Право на оплату спрашивается у матрицы контракта, а не у списка действий:
  // имён вроде `billing.write` контракт не определяет, а ошибка в имени здесь
  // прячет не кнопку, а весь смысл экрана.
  const canPay = access.reaches('POST', '/billing/subscription/plan');
  // Счёт выставляется, пока подписка не заработала: её нет вовсе, она истекла
  // или ждёт первую оплату. Последний случай — это перевыставление, и правило
  // здесь ровно то же, что на экране оформления (`checkout-gate.ts`): не
  // показывать путь, который сервер отобьёт, и не запирать тот, который он ведёт.
  const invoiceOpen =
    !subscription ||
    subscription.status === 'expired' ||
    subscription.status === 'pending';
  const hasOpenInvoice = (portal.recent_documents ?? []).some(
    (document) => document.kind === 'invoice' && document.status === 'issued',
  );

  const price = (plan: PlanRow) =>
    period === 'year' ? plan.yearly_price_kopecks : plan.monthly_price_kopecks;

  return (
    <>
      {head}

      {failure ? <ErrorNotice error={failure} /> : null}
      {note ? (
        <p className="notice ok" role="status">
          <b>Готово</b>
          {note}
        </p>
      ) : null}

      {/* Сводка до подробностей: что куплено, до какого числа и что с замерами. */}
      <section className="summary">
        <div>
          <div className="value">
            {subscription ? subscription.plan_title : 'Подписка не оформлена'}
          </div>
          <div className="status">
            {/* Латинского кода статуса на экране быть не может: пришёл незнакомый —
                значит, о состоянии сказать нечего, и честнее промолчать, чем
                показать `past_due`. */}
            {subscription
              ? (STATUS_WORDS[subscription.status] ?? 'Состояние уточняется')
              : 'Замеры начнутся после оплаты'}
          </div>
        </div>

        {/* «Не измерено» — слово для показателя, которого не удалось замерить.
            Цена не измеряется, она либо назначена тарифом, либо тарифа ещё нет,
            и «Не измерено в месяц» на первом экране оплаты не значило ничего.
            Пока подписки нет, эта клетка не показывается вовсе: сетка тарифов
            прямо под ней и называет все цены. */}
        {current && price(current) !== null && price(current) !== undefined ? (
          <div>
            <div className="h3">{moneyKopecks(price(current)!)}</div>
            <div className="stamp">
              {subscription?.period === 'year' ? 'в год' : 'в месяц'}
            </div>
          </div>
        ) : null}

        <p className="stamp basis">
          {subscription?.current_period_start && subscription.current_period_end
            ? `Расчётный период с ${dateProse(subscription.current_period_start)} по ${dateProse(subscription.current_period_end)}`
            : 'Оплаченного периода пока нет'}
          {/* Списание обещаем, только если его действительно кто-то выполнит.
              Дата в поле `next_charge_at` живёт и при отозванном согласии — это
              «когда закончится оплаченное», а не «когда снимем деньги». Назвать
              её списанием при выключенном автопродлении значит соврать. */}
          {subscription?.next_charge_at && !subscription.cancel_at_period_end
            ? autopayOn
              ? ` · следующее списание ${dateProse(subscription.next_charge_at)} (${daysLeft(subscription.next_charge_at)})`
              : ` · продлить вручную до ${dateProse(subscription.next_charge_at)} (${daysLeft(subscription.next_charge_at)})`
            : ''}
          {subscription?.payment_method_title
            ? ` · ${subscription.payment_method_title}`
            : ''}
        </p>
      </section>

      {/* Отмена, запланированное понижение и остановленные замеры — состояния,
          а не ошибки, и раскрываются они на этом же экране. */}
      {subscription?.cancel_at_period_end ? (
        <div className="notice warn" role="status">
          <b>Подписка отменена</b>
          Замеры идут до {dateProse(subscription.current_period_end ?? '')} — оплаченное
          мы не отбираем. После этой даты новые ответы движков не собираются, а собранные
          числа остаются на экранах.
          {canPay ? (
            <span className="notice-act">
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                disabled={busy === 'resume'}
                onClick={() =>
                  act('resume', async () => {
                    await resumeSubscription();
                    return 'Отмена снята, подписка продолжится.';
                  })
                }
              >
                Вернуть подписку
              </button>
            </span>
          ) : null}
        </div>
      ) : null}

      {subscription?.pending_plan_code ? (
        <div className="notice" role="status">
          <b>Тариф сменится со следующего периода</b>С{' '}
          {dateProse(
            subscription.pending_effective_at ?? subscription.current_period_end ?? '',
          )}{' '}
          вы перейдёте на «
          {plans.find((p) => p.code === subscription.pending_plan_code)?.title ??
            subscription.pending_plan_code}
          ». До конца оплаченного периода всё остаётся как есть.
          {canPay ? (
            <span className="notice-act">
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                disabled={busy === 'pending'}
                onClick={() =>
                  act('pending', async () => {
                    await cancelPendingPlanChange();
                    return 'Смена тарифа отменена.';
                  })
                }
              >
                Отменить смену
              </button>
            </span>
          ) : null}
        </div>
      ) : null}

      {!quotas.scan_allowed ? (
        <div className="notice" role="status">
          <b>Замеры остановлены</b>
          {quotas.scan_blocked_reason ?? scanBlockedFallback()} Уже собранные числа никуда
          не денутся.
        </div>
      ) : null}

      {/* --- расход --- */}
      <Panel
        title="Расход текущего периода"
        lead="Расход считается за расчётный период, а не за календарный месяц."
      >
        <div className="meters">
          <Meter
            label="Отслеживаемые вопросы"
            used={quotas.questions_used ?? 0}
            limit={quotas.tracked_questions}
            unit={['вопрос', 'вопроса', 'вопросов']}
            hint="Единица тарификации: один вопрос покупателя, проверяемый во всех движках сразу."
          />
          <Meter
            label="Рабочие пространства"
            used={quotas.workspaces_used ?? 0}
            limit={quotas.max_workspaces}
            unit={['пространство', 'пространства', 'пространств']}
            hint="Одно пространство — один клиент агентства или одно направление бизнеса."
          />
          {answers ? (
            <Meter
              label="Ответы движков за расчётный период"
              used={answers.used}
              limit={answers.limit ?? null}
              unit={['ответ', 'ответа', 'ответов']}
              hint="Кончится он — замеры встанут до следующего расчётного периода."
            />
          ) : null}
        </div>

        <p className="prose-note">
          <b>Когда квота кончится</b> — новые замеры перестанут запускаться. Всё собранное
          останется на экранах и в выгрузках. Замеры вернёт докупка объёма или старший
          тариф, с того же дня.
        </p>
      </Panel>

      {/* --- что входит --- */}
      {current ? (
        <Panel title={`Что входит в тариф «${current.title}»`}>
          <Kv>
            <KvRow label="Отслеживаемых вопросов">{int(current.tracked_questions)}</KvRow>
            <KvRow label="Рабочих пространств">{int(current.max_workspaces)}</KvRow>
            <KvRow label="Конкурентов">
              {current.max_competitors === null || current.max_competitors === undefined
                ? 'без лимита'
                : int(current.max_competitors)}
            </KvRow>
            <KvRow label="История" note="Точки старше этого срока не хранятся">
              {countOf(current.history_months, 'месяц', 'месяца', 'месяцев')}
            </KvRow>
            <KvRow label="Движки и сотрудники">Все, без доплаты</KvRow>
            {current.white_label ? (
              <KvRow label="Отчёты под своим брендом">Входит</KvRow>
            ) : null}
            {current.client_portal ? (
              <KvRow label="Портал для клиентов на своём домене">Входит</KvRow>
            ) : null}
            {/* «Доступ к данным машиной» — попытка сказать «API» по-русски, от
                которой слово стало непонятнее английского: витрина эту строку
                по той же причине сняла (комментарий у тарифа «Масштаб» в
                `product.ts`). Покупатель здесь — маркетолог агентства, и «API»
                он знает. */}
            {current.data_api ? (
              <KvRow label="Доступ к данным по API">Входит</KvRow>
            ) : null}
          </Kv>
        </Panel>
      ) : null}

      {/* --- сетка тарифов --- */}
      <Panel
        title="Тарифы"
        lead="Годовая оплата равна десяти месячным — два месяца в подарок."
        action={
          <fieldset className="seg-radio">
            <legend className="visually-hidden">Период оплаты</legend>
            {(['month', 'year'] as const).map((unit) => (
              <label key={unit}>
                <input
                  type="radio"
                  name="billing-period"
                  value={unit}
                  checked={period === unit}
                  onChange={() => setPeriod(unit)}
                />
                <span>{unit === 'month' ? 'Помесячно' : 'На год'}</span>
              </label>
            ))}
          </fieldset>
        }
      >
        <div className="price-grid">
          {plans.map((plan) => {
            // «Действует сейчас» — про деньги, а не про совпадение кода тарифа.
            // Здесь сравнивались только код и период, и неоплаченная подписка
            // получала тот же штамп: клиент видел одновременно «ЖДЁМ ПЕРВЫЙ
            // ПЛАТЁЖ», «Действует сейчас» и квоты 0 из 0.
            const paid =
              subscription?.status === 'active' || subscription?.status === 'past_due';
            const here =
              plan.code === current?.code && period === subscription?.period && paid;
            const awaiting =
              plan.code === current?.code &&
              period === subscription?.period &&
              subscription?.status === 'pending';
            const value = price(plan);
            const upgrade = current ? order(plan) > order(current) : true;
            return (
              <div
                key={plan.code}
                className={`tier${order(plan) > 3 ? ' wide' : ''}${here ? ' feat' : ''}`}
              >
                {here ? <div className="tier-tag">Ваш тариф</div> : null}
                <div className="tier-name">{plan.title}</div>
                <div className="tier-price">
                  {value === null || value === undefined ? (
                    <>
                      от 199 000 ₽<small>/мес</small>
                    </>
                  ) : (
                    <>
                      {moneyKopecks(value)}
                      <small>{period === 'year' ? '/год' : '/мес'}</small>
                    </>
                  )}
                </div>
                <div className="tier-desc">
                  {countOf(
                    plan.tracked_questions,
                    'отслеживаемый вопрос',
                    'отслеживаемых вопроса',
                    'отслеживаемых вопросов',
                  )}{' '}
                  · {countOf(plan.history_months, 'месяц', 'месяца', 'месяцев')} истории
                </div>

                {here ? (
                  <p className="stamp tier-here">Действует сейчас</p>
                ) : awaiting && canPay ? (
                  // Оплата брошена на полпути — обычное дело: закрыл вкладку
                  // ЮKassa. Продолжить было нечем: кнопки не было нигде, а
                  // повторное оформление отбивалось 409.
                  <Link
                    className="btn btn-primary"
                    to={`/plan/checkout?plan=${plan.code}&period=${period}`}
                  >
                    Продолжить оплату
                  </Link>
                ) : canPay && subscription && paid ? (
                  <button
                    className={`btn ${upgrade ? 'btn-primary' : 'btn-ghost'}`}
                    type="button"
                    disabled={busy === `plan-${plan.code}` || value === null}
                    // Щелчок больше не покупает. Он спрашивает сервер, во что
                    // это обойдётся, и показывает ответ — покупка происходит
                    // вторым, осознанным нажатием в окне подтверждения.
                    onClick={() => {
                      const mine = (asked.current += 1);
                      setIntent({ kind: 'counting', plan });
                      previewPlanChange(plan.code, period)
                        .then((preview) => {
                          if (asked.current === mine)
                            setIntent({ kind: 'ready', plan, preview });
                        })
                        .catch((error: unknown) => {
                          if (asked.current === mine)
                            setIntent({ kind: 'failed', plan, error });
                        });
                    }}
                  >
                    {value === null
                      ? 'Обсудить объём'
                      : upgrade
                        ? 'Перейти сейчас'
                        : 'Понизить со следующего периода'}
                  </button>
                ) : canPay && !plan.is_self_service ? (
                  // Договорный тариф картой не продаётся: цена на витрине
                  // объявлена как «от», то есть обсуждается вместе с объёмом
                  // работ. Страница оформления открывала обычную платёжную
                  // панель с фиксированной суммой и активной кнопкой оплаты.
                  //
                  // Разговор идёт в телеграме, как и с витрины. Тема письма
                  // была вписана буквой — «Управляемая аналитика», — а кнопка
                  // рисуется у каждого договорного тарифа: у второго такого
                  // человек уходил обсуждать чужой. Черновик собирается из
                  // названия того тарифа, у которого нажали (`?text=` в ссылке
                  // на учётную запись — core.telegram.org/api/links).
                  <a
                    className="btn btn-ghost"
                    href={`${LEGAL_ENTITY.telegramHref}?text=${encodeURIComponent(
                      `Здравствуйте! Хочу обсудить тариф «${plan.title}».`,
                    )}`}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Обсудить условия
                  </a>
                ) : canPay ? (
                  <Link
                    className="btn btn-primary"
                    to={`/plan/checkout?plan=${plan.code}&period=${period}`}
                  >
                    Оформить подписку
                  </Link>
                ) : null}

                <ul className="feats">
                  <li>
                    <Check />
                    {countOf(
                      plan.max_workspaces,
                      'рабочее пространство',
                      'рабочих пространства',
                      'рабочих пространств',
                    )}
                  </li>
                  <li>
                    <Check />
                    {plan.max_competitors === null || plan.max_competitors === undefined
                      ? 'конкуренты без лимита'
                      : countOf(
                          plan.max_competitors,
                          'конкурент',
                          'конкурента',
                          'конкурентов',
                        )}
                  </li>
                  {plan.white_label ? (
                    <li>
                      <Check />
                      отчёты под своим брендом
                    </li>
                  ) : null}
                  {plan.client_portal ? (
                    <li>
                      <Check />
                      портал для клиентов на своём домене
                    </li>
                  ) : null}
                  {plan.data_api ? (
                    <li>
                      <Check />
                      доступ к данным по API
                    </li>
                  ) : null}
                </ul>
              </div>
            );
          })}
        </div>

        <p className="prose-note">
          Повышение тарифа действует сразу: расчётный период начинается заново, квоты
          обнуляются, деньги пересчитываются пропорционально. Понижение вступает в силу со
          следующего периода.
        </p>
      </Panel>

      {/* --- докупки --- */}
      <Panel
        title="Докупки"
        lead="Расширяют лимиты текущего периода сразу, в следующий счёт попадают пропорционально остатку периода."
      >
        {portal.addons && portal.addons.length > 0 ? (
          <ul className="rows">
            {portal.addons.map((addon) => (
              <li key={addon.id}>
                <span>
                  <b>{addon.title}</b>
                  <span className="stamp">
                    подключено {dateProse(addon.added_at)} ·{' '}
                    {moneyKopecks(addon.monthly_price_kopecks ?? 0)} в месяц
                  </span>
                </span>
                {canPay ? (
                  <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    disabled={busy === `addon-${addon.id}`}
                    onClick={() => setRemovingAddon(addon.id)}
                  >
                    Отключить
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <p className="prose-note">Докупок нет. Тариф работает в своих лимитах.</p>
        )}

        {/* Список продаваемых докупок берётся из каталога. Здесь стояли две
            кнопки с зашитыми кодами `weekly_50` и `daily_25`, и половина
            позиций §10 не продавалась вовсе. Докупка, требующая другого тарифа,
            показывается только тому, у кого этот тариф есть. */}
        {canPay && subscription ? (
          <div className="row-actions">
            {(catalog?.addons ?? [])
              .filter(
                (addon) =>
                  !addon.requires_plan_code ||
                  addon.requires_plan_code === subscription.plan_code,
              )
              .map((addon) => (
                <button
                  key={addon.code}
                  className="btn btn-ghost btn-sm"
                  type="button"
                  disabled={busy === `addon-${addon.code}`}
                  onClick={() =>
                    act(`addon-${addon.code}`, async () => {
                      const key = idempotencyKey();
                      await addAddon(addon.code, key);
                      return `«${addon.title}» — уже действует.`;
                    })
                  }
                >
                  {addon.title} · {moneyKopecks(addon.monthly_price_kopecks)}/мес
                </button>
              ))}
          </div>
        ) : null}
      </Panel>

      {/* --- автосписание --- */}
      {portal.autopay ? (
        <Panel
          title="Автосписание"
          lead="Списывать можно только при действующем согласии и уведомлении, отправленном не позже чем за сутки, — этого требует 376-ФЗ."
        >
          <Kv>
            {/* Способ оплаты стоит ПЕРВЫМ, потому что из него и из согласия
                считается всё остальное на панели: без карты автосписание
                невозможно, чем бы ни было согласие. Строка показывается в обеих
                ветках — и когда карта есть, и когда её нет: панель не прыгает
                после отвязки, и результат человек читает там же, где секунду
                назад стояло название карты. */}
            <KvRow
              label="Способ оплаты"
              note={
                portal.autopay.payment_method_saved
                  ? 'номер карты мы не видим — у нас хранится только разрешение ЮKassa списывать по ней'
                  : 'списывать нечем — подписка сама не продлится'
              }
            >
              {portal.autopay.payment_method_saved
                ? (portal.autopay.payment_method_title ?? 'Сохранён')
                : 'Не сохранён'}
            </KvRow>
            <KvRow label="Состояние">
              {portal.autopay.enabled ? 'Включено' : 'Выключено'}
            </KvRow>
            <KvRow
              label="Согласие"
              note={
                portal.autopay.consent_granted_at
                  ? `дано ${dateProse(portal.autopay.consent_granted_at)}${
                      portal.autopay.consent_text_version
                        ? `, редакция ${portal.autopay.consent_text_version}`
                        : ''
                    }`
                  : undefined
              }
            >
              {portal.autopay.consent_granted
                ? 'Действует'
                : 'Не давали — продление вручную'}
            </KvRow>
            {/* Пока автопродление выключено, «следующее списание» — обещание,
                которое некому исполнить. Показываем строку только когда деньги
                действительно спишутся сами.

                Письма перед концом периода у нас нет — и в примечании «Продления»
                его больше не обещаем: единственное письмо биллинга это
                уведомление о предстоящем автосписании, а оно уходит только
                подписке с включённым автопродлением
                (`billing.claim_subscriptions_for_notice` берёт `auto_renew`).
                Обещание было неправдой ровно для тех, кому адресовано, — для
                тех, у кого автопродление снято. */}
            {autopayOn ? (
              <KvRow label="Следующее списание">
                {portal.autopay.next_charge_at
                  ? `${dateProse(portal.autopay.next_charge_at)}${
                      portal.autopay.next_charge_amount_kopecks
                        ? ` · ${moneyKopecks(portal.autopay.next_charge_amount_kopecks)}`
                        : ''
                    }`
                  : 'Назначим, когда начнётся следующий период'}
              </KvRow>
            ) : (
              <KvRow
                label="Продление"
                note="Оплатить следующий период можно на этом экране, когда закончится оплаченный"
              >
                Вручную
              </KvRow>
            )}
            {autopayOn ? (
              <KvRow
                label="Уведомление за сутки"
                note="Без отправленного уведомления списание не выполняется"
              >
                {portal.autopay.notice_sent_at
                  ? momentProse(portal.autopay.notice_sent_at)
                  : 'Отправим за сутки до списания'}
              </KvRow>
            ) : null}
          </Kv>

          {/* Две кнопки, потому что это два разных действия. Отзыв согласия
              снимает разрешение по 376-ФЗ, отвязка убирает карту, и одно без
              другого — обычное состояние: согласие переживает удаление карты.
              Кнопки нет там, где ей нечего делать: отвязка без сохранённого
              способа отвечает 404, а показывать контрол, которым нельзя
              воспользоваться, запрещено (app-kit.md §1). */}
          {canPay &&
          (portal.autopay.consent_granted || portal.autopay.payment_method_saved) ? (
            <div className="row-actions">
              {portal.autopay.consent_granted ? (
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  disabled={busy === 'autopay'}
                  onClick={() => setRevokingConsent(true)}
                >
                  Отозвать согласие
                </button>
              ) : null}
              {portal.autopay.payment_method_saved ? (
                <button
                  className="btn btn-danger btn-sm"
                  type="button"
                  disabled={busy === 'payment-method'}
                  onClick={() => setDeletingMethod(true)}
                >
                  Удалить способ оплаты
                </button>
              ) : null}
            </div>
          ) : null}
        </Panel>
      ) : null}

      {/* --- платежи и документы --- */}
      <Panel
        title="Платежи"
        lead="Оплата по счёту — обычный путь для юрлица: подписка включается по факту поступления."
      >
        {portal.recent_payments && portal.recent_payments.length > 0 ? (
          <div className="tbl-wrap">
            <table className="mini">
              <caption className="stamp">
                Последние платежи организации. Возврат со страницы оплаты оплату не
                подтверждает: состояние платежа мы узнаём от банка.
              </caption>
              <thead>
                <tr>
                  <th scope="col">Дата</th>
                  <th scope="col">Назначение</th>
                  <th scope="col">Состояние</th>
                  <th scope="col" className="right">
                    Сумма
                  </th>
                  <th scope="col" className="right">
                    Действие
                  </th>
                </tr>
              </thead>
              <tbody>
                {portal.recent_payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="tnum">{dateProse(payment.created_at)}</td>
                    <td>{payment.description ?? 'Подписка'}</td>
                    <td>
                      {payment.status === 'succeeded' ? (
                        <span className="pill up">оплачен</span>
                      ) : payment.status === 'canceled' ? (
                        <span className="pill down">отклонён</span>
                      ) : (
                        <span className="pill flat">ждём подтверждения</span>
                      )}
                    </td>
                    <td className="tnum right">{moneyKopecks(payment.amount_kopecks)}</td>
                    <td className="right">
                      <PendingPaymentActions payment={payment} onChange={reload} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="prose-note">Платежей ещё не было.</p>
        )}
      </Panel>

      {/* Раздел показывается ВСЕГДА, а не только при наличии документов.
          Пока он прятался на пустом списке, обещание соседней панели («оплата
          по счёту — обычный путь для юрлица») висело в воздухе: приёмка искала
          кнопку выставления счёта и не нашла её нигде в кабинете. Пустой раздел
          с одной кнопкой честнее спрятанного. */}
      <Panel title="Счета и закрывающие" lead={PAYER_SNAPSHOT}>
        {portal.recent_documents && portal.recent_documents.length > 0 ? (
          <div className="tbl-wrap">
            <table className="mini">
              <caption className="stamp">Документы организации, новые сверху</caption>
              <thead>
                <tr>
                  <th scope="col">Документ</th>
                  <th scope="col">Период</th>
                  <th scope="col">Состояние</th>
                  <th scope="col" className="right">
                    Сумма
                  </th>
                  <th scope="col" className="right">
                    Файл
                  </th>
                </tr>
              </thead>
              <tbody>
                {portal.recent_documents.map((document) => (
                  <tr key={document.id}>
                    <td>
                      {document.kind === 'invoice'
                        ? 'Счёт'
                        : document.kind === 'act'
                          ? 'Акт'
                          : 'УПД'}{' '}
                      № {document.number}
                    </td>
                    <td className="tnum">
                      {document.period_start && document.period_end
                        ? `${dateProse(document.period_start)} — ${dateProse(document.period_end)}`
                        : NOT_MEASURED}
                    </td>
                    {/* Аннулированный счёт, показанный «выставленным», — это
                        платёжное поручение по документу, который мы уже отозвали.
                        Состояние называется своим словом, а у живого счёта рядом
                        стоит срок: он настоящий, просроченный аннулируется. */}
                    <td>
                      {DOCUMENT_STATE[document.status] ?? document.status}
                      {document.status === 'issued' && document.due_on ? (
                        <span className="stamp">
                          {' '}
                          · оплатить до {dateProse(document.due_on)}
                        </span>
                      ) : null}
                    </td>
                    <td className="tnum right">
                      {moneyKopecks(document.amount_kopecks)}
                    </td>
                    <td className="right">
                      <DocumentDownload documentId={document.id} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="prose-note">Документов пока нет.</p>
        )}

        {/* Счёт открывает подписку, поэтому выставляется он до неё. Кнопка
            показывается и при `pending`: незакрытая первая оплата — это как раз
            тот случай, когда счёт нужно ПЕРЕВЫСТАВИТЬ (опечатка в ИНН, сменилось
            юрлицо), и сервер это умеет — новый счёт заменяет прежний, вторая
            подписка не заводится. Когда подписка уже работает, сервер отвечает
            409, и кнопку, которая гарантированно даёт отказ, показывать нельзя. */}
        {canPay && invoiceOpen ? (
          <div className="row-actions">
            {/* Тариф и период уезжают в адрес, а не берутся по умолчанию.
                Перевыставление ПЕРЕПИСЫВАЕТ незакрытую подписку значениями
                запроса: без них годовой «Партнёр», ожидающий оплаты, молча
                стал бы месячным «Ростом» — экран оформления начинает с него. */}
            <Link
              className="btn btn-primary"
              to={`/plan/checkout?method=invoice${
                subscription
                  ? `&plan=${encodeURIComponent(subscription.plan_code)}&period=${encodeURIComponent(subscription.period)}`
                  : ''
              }`}
            >
              {hasOpenInvoice ? 'Перевыставить счёт' : 'Выставить счёт на юрлицо'}
            </Link>
          </div>
        ) : null}
        {canPay && invoiceOpen && hasOpenInvoice ? (
          <p className="stamp">
            Новый счёт заменит выставленный: прежний станет аннулированным, оплачивать
            нужно будет только новый.
          </p>
        ) : null}
        {canPay && !invoiceOpen ? (
          <p className="stamp">
            Счёт выставляется при оформлении подписки — он её и открывает. Счёт на
            следующий период приходит на почту организации перед его началом.
          </p>
        ) : null}
      </Panel>

      {/* --- отмена --- */}
      {canPay && subscription && !subscription.cancel_at_period_end ? (
        <Panel
          title="Отмена подписки"
          lead="Из кабинета и без звонков. Подписка доживает до конца оплаченного периода — замеры до этого дня идут."
        >
          <div className="row-actions">
            <button
              className="btn btn-danger btn-sm"
              type="button"
              onClick={() => setCancelling(true)}
            >
              Отменить подписку
            </button>
          </div>
        </Panel>
      ) : null}

      {/*
        Подтверждение смены тарифа. Показывает три вещи, которых до него не было
        нигде: сколько списывают СЕЙЧАС, почему именно столько (зачёт за
        неоплаченный остаток текущего периода) и что меняется в лимитах.
        Числа приходят с сервера тем же расчётом, который выполнит сама смена, —
        считать их на экране нельзя: пропорция зависит от границ расчётного
        периода и от фактически уплаченного за него.
      */}
      <Dialog
        open={intent !== null}
        title={intent ? `Переход на «${intent.plan.title}»` : 'Смена тарифа'}
        onClose={() => {
          asked.current += 1;
          setIntent(null);
        }}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => {
                asked.current += 1;
                setIntent(null);
              }}
            >
              Отмена
            </button>
            {intent?.kind === 'ready' ? (
              <button
                className="btn btn-primary btn-sm"
                type="button"
                disabled={busy === `plan-${intent.plan.code}`}
                onClick={() => {
                  const { plan, preview } = intent;
                  asked.current += 1;
                  setIntent(null);
                  void act(`plan-${plan.code}`, async () => {
                    // Ключ создаётся до вызова и живёт в замыкании: один щелчок
                    // по подтверждению — одна попытка списания, даже если вызов
                    // придётся повторить.
                    const key = idempotencyKey();
                    const result = await changePlan(plan.code, preview.period, key);
                    if (result.payment?.confirmation_url) {
                      // Запоминаем платёж перед уходом на платёжку — ровно как
                      // это делает оформление первой подписки. Здесь этого не
                      // было вовсе, и на возврате `sessionStorage` отдавал
                      // БРОШЕННЫЙ платёж: экран возврата показывал чужое
                      // состояние под заголовком «Платёж». Основной путь
                      // надёжнее — `payment_id` дописывает сервер в адрес
                      // возврата, — но вкладка может вернуться и без параметра.
                      rememberPayment(result.payment.payment_id);
                      window.location.assign(result.payment.confirmation_url);
                      return null;
                    }
                    return result.applied
                      ? `Тариф «${plan.title}» действует с сегодняшнего дня.`
                      : `Переход на «${plan.title}» запланирован на ${dateProse(result.effective_at ?? '')}.`;
                  });
                }}
              >
                {intent.preview.requires_payment
                  ? `Оплатить ${moneyKopecks(intent.preview.charge_kopecks)}`
                  : intent.preview.direction === 'downgrade'
                    ? 'Запланировать понижение'
                    : 'Перейти сейчас'}
              </button>
            ) : null}
          </>
        }
      >
        {intent?.kind === 'counting' ? <Skeleton rows={2} /> : null}
        {intent?.kind === 'failed' ? <ErrorNotice error={intent.error} /> : null}
        {intent?.kind === 'ready' ? (
          <>
            <p className="dlg-lead">
              {intent.preview.direction === 'downgrade' ? (
                <>
                  Понижение вступит в силу{' '}
                  {intent.preview.effective_at
                    ? dateProse(intent.preview.effective_at)
                    : 'в конце оплаченного периода'}
                  . До этой даты работает нынешний тариф — деньги за оплаченный период не
                  пропадают.
                </>
              ) : intent.preview.requires_payment ? (
                <>
                  Новый тариф начнёт действовать сразу после оплаты. Списывается только
                  разница за остаток текущего периода, а не полная цена: уже уплаченное
                  зачитывается.
                </>
              ) : (
                <>
                  Переход бесплатен: зачёта за остаток оплаченного периода хватает
                  целиком. Новый тариф начнёт действовать сразу.
                </>
              )}
            </p>
            <Kv>
              {intent.preview.direction === 'upgrade' ? (
                <>
                  <KvRow label="К оплате сейчас" note="за остаток текущего периода">
                    {moneyKopecks(intent.preview.charge_kopecks)}
                  </KvRow>
                  <KvRow label="Зачтено за остаток" note="уплаченное по нынешнему тарифу">
                    {moneyKopecks(intent.preview.credit_kopecks)}
                  </KvRow>
                </>
              ) : null}
              <KvRow
                label="Дальше по тарифу"
                note={intent.preview.period === 'year' ? 'в год' : 'в месяц'}
              >
                {moneyKopecks(intent.preview.full_price_kopecks)}
              </KvRow>
              <KvRow label="Отслеживаемых вопросов">
                {int(intent.preview.current_plan.tracked_questions)} →{' '}
                {int(intent.preview.new_plan.tracked_questions)}
              </KvRow>
              <KvRow label="Глубина истории">
                {countOf(
                  intent.preview.current_plan.history_months,
                  'месяц',
                  'месяца',
                  'месяцев',
                )}{' '}
                →{' '}
                {countOf(
                  intent.preview.new_plan.history_months,
                  'месяц',
                  'месяца',
                  'месяцев',
                )}
              </KvRow>
              <KvRow label="Рабочих пространств">
                {int(intent.preview.current_plan.max_workspaces)} →{' '}
                {int(intent.preview.new_plan.max_workspaces)}
              </KvRow>
            </Kv>
          </>
        ) : null}
      </Dialog>

      <Confirm
        open={revokingConsent}
        title="Отозвать согласие на автосписание"
        action="Отозвать согласие"
        onConfirm={() => {
          setRevokingConsent(false);
          void act('autopay', async () => {
            await revokeAutopayConsent();
            return 'Согласие отозвано, автопродление снято. Подписку можно продлевать вручную.';
          });
        }}
        onCancel={() => setRevokingConsent(false)}
      >
        Подписка перестанет продлеваться сама. Замеры идут до конца оплаченного периода,
        дальше её нужно продлить вручную — оплатить следующий период на этом экране.
      </Confirm>

      <Confirm
        open={deletingMethod}
        title="Удалить способ оплаты"
        action="Удалить способ оплаты"
        danger
        onConfirm={() => {
          setDeletingMethod(false);
          void act('payment-method', async () => {
            try {
              await confirmed('billing.delete_payment_method', (token) =>
                deletePaymentMethod(token),
              );
            } catch (error) {
              // `404` здесь — не отказ, а тот же самый результат, полученный
              // раньше: карту убрали из соседней вкладки или с другого
              // устройства. Показать «Способ оплаты не сохранён» ошибкой и
              // оставить на экране название карты с живой кнопкой удаления
              // значит соврать дважды подряд. Экран перечитывается, человек
              // читает положение дел, а не отказ.
              if (!(error instanceof ApiError) || error.code !== 'not_found') throw error;
              return 'Способ оплаты уже был отвязан — сейчас у подписки его нет.';
            }
            return 'Способ оплаты удалён. Автопродление снято — продлевать подписку теперь нужно вручную.';
          });
        }}
        onCancel={() => setDeletingMethod(false)}
      >
        <p className="dlg-lead">
          Мы храним не номер карты, а разрешение ЮKassa списывать по ней. Удаление снимает
          это разрешение вместе с автопродлением.
        </p>
        <p className="dlg-lead">
          Подписка доработает оплаченный период — замеры идут до{' '}
          {subscription?.current_period_end
            ? dateProse(subscription.current_period_end)
            : 'конца оплаченного периода'}
          . Чтобы вернуть автосписание, оплатите следующий период картой и снова сохраните
          её.
        </p>
      </Confirm>

      <Confirm
        open={removingAddon !== null}
        title="Отключить докупку"
        action="Отключить докупку"
        onConfirm={() => {
          const id = removingAddon;
          setRemovingAddon(null);
          if (id) {
            void act(`addon-${id}`, async () => {
              await removeAddon(id);
              return 'Докупка отключится в конце текущего периода — оплаченное не отбираем.';
            });
          }
        }}
        onCancel={() => setRemovingAddon(null)}
      >
        Докупка доработает до конца текущего периода, дальше лимит вернётся к тарифному.
        Уплаченное за этот период не отбираем.
      </Confirm>

      <Dialog
        open={cancelling}
        title="Отменить подписку"
        onClose={() => setCancelling(false)}
        footer={
          <>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={() => setCancelling(false)}
            >
              Оставить подписку
            </button>
            <button
              className="btn btn-danger btn-sm"
              type="button"
              disabled={busy === 'cancel'}
              onClick={() => {
                setCancelling(false);
                void act('cancel', async () => {
                  await confirmed('billing.cancel_subscription', (token) =>
                    cancelSubscription(reason, token),
                  );
                  return 'Подписка отменена. Замеры идут до конца оплаченного периода.';
                });
              }}
            >
              Отменить подписку
            </button>
          </>
        }
      >
        {/* Эта кнопка отменяет ПРОДЛЕНИЕ, а денег не возвращает: контракт так её
            и описывает. Оферта при этом даёт право отказаться в любой момент и
            получить назад плату за оставшиеся дни — по письму. Человек, нажавший
            самую заметную кнопку, молча терял то, что тот же документ ему
            обещал. Два разных действия обязаны быть названы обоими именами на
            том экране, где выбирают.

            Абзац стоит ПЕРВЫМ. Вторым его не читают: над ним лежала спокойная
            строка про то, что замеры доживут период, и глаз уходил на кнопку. */}
        <p className="dlg-lead">
          Деньги за оставшиеся дни эта кнопка не возвращает: подписка просто доживает
          оплаченный период. Если нужен возврат, напишите на{' '}
          <a href={`mailto:${LEGAL_ENTITY.contact}`}>{LEGAL_ENTITY.contact}</a> — по
          оферте плата за дни после отказа возвращается тем же способом в течение десяти
          календарных дней.
        </p>
        <p className="dlg-lead">
          Замеры остановятся{' '}
          {subscription?.current_period_end
            ? dateProse(subscription.current_period_end)
            : 'в конце оплаченного периода'}
          . Собранные результаты и отчёты останутся доступны, новые ответы движков
          собираться перестанут. Вернуть подписку можно в один клик до этой даты.
        </p>
        <div className="field">
          <label htmlFor="cancel-reason">Почему уходите</label>
          <textarea
            id="cancel-reason"
            value={reason}
            rows={3}
            onChange={(event) => setReason(event.target.value)}
          />
          <span className="hint">Не обязательно. Читаем и отвечаем сами.</span>
        </div>
      </Dialog>
    </>
  );
}

/**
 * Скачивание документа кнопкой, а не ссылкой.
 *
 * Ссылка `target="_blank"` уводила приёмку в новую вкладку с голым
 * `{"detail":"Файл документа ещё не сформирован."}`: на такой ссылке браузер не
 * показывает отказ никак, он просто рисует его тело. Запрос идёт вызовом, и
 * отказ выводится словами рядом с кнопкой — как в центре отчётов.
 */
function DocumentDownload({ documentId }: { documentId: string }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const download = () => {
    setBusy(true);
    setFailure(null);
    downloadDocument(documentId)
      .catch(setFailure)
      .finally(() => setBusy(false));
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={download}
        disabled={busy}
      >
        {busy ? 'Готовим…' : 'Скачать'}
      </button>
      {failure ? <ErrorNotice error={failure} /> : null}
    </>
  );
}

/**
 * Выход из брошенного платежа.
 *
 * Клиент, ушедший с ЮKassa кнопкой «назад», видел строку «ждём подтверждения»
 * без единого действия: продолжить нечем, отменить нечем, а повторная оплата
 * отвечала «уже есть незавершённый платёж». Единственным выходом было ждать
 * сутки, пока платёж протухнет у провайдера.
 *
 * Отсюда две кнопки. «Продолжить оплату» уводит по живой ссылке подтверждения —
 * она приходит в строке платежа и остаётся действительной у провайдера.
 * «Отменить» закрывает платёж; сервер перед этим сверяется с ЮKassa, поэтому
 * оплаченный платёж не отменится, а вернёт `409` и включит подписку.
 */
function PendingPaymentActions({
  payment,
  onChange,
}: {
  payment: Payment;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  if (payment.status !== 'pending' && payment.status !== 'waiting_for_capture') {
    return null;
  }

  const cancel = () => {
    setBusy(true);
    setFailure(null);
    cancelPayment(payment.id)
      .then(onChange)
      .catch((error: unknown) => {
        setFailure(error);
        // Платёж мог всё-таки пройти: тогда сервер отвечает 409, и экран
        // обязан показать уже оплаченную подписку, а не старую строку.
        onChange();
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      <span className="row-actions">
        {payment.confirmation_url ? (
          <a
            className="btn btn-ghost btn-sm"
            href={payment.confirmation_url}
            rel="noopener noreferrer"
          >
            Продолжить оплату
          </a>
        ) : null}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={cancel}
          disabled={busy}
        >
          {busy ? 'Отменяем…' : 'Отменить'}
        </button>
      </span>
      {failure ? <ErrorNotice error={failure} /> : null}
    </>
  );
}
